import { RatePlan, RatePlanCharge } from '../ratePlan';
import getCurrentTimezoneDate from './getCurrentTimezoneDate';
import getCurrentTimezoneDaysDiff from './getCurrentTimezoneDaysDiff';
import { hasRatePlanActiveDiscounts } from './hasRatePlanActiveDiscounts';
import { hasRatePlanQtyChangeAmendmends } from './hasRatePlanQtyChangeAmendmends';

export interface SubscriptionConfig {
  ratePlans_TimeZone: string;
}

interface SubscriptionRatePlan {
  id: RatePlan['id'];
  ratePlanCharges: RatePlanCharge[];
  lastChangeType?: 'Remove' | 'Add' | 'Update';
}
export interface Subscription {
  status: string;
  ratePlans: SubscriptionRatePlan[];
  latestRateplans: SubscriptionRatePlan[];
  maxAllowedQuantity?: number;
  minAllowedQuantity?: number;
  defaultProductAutoRenew: boolean;
  autoRenew: boolean;
  termEndDate: string;
  renewalTerm: 1 | 12;
}

export const makeSubscriptionCapacities = (
  subscription: Subscription,
  config: SubscriptionConfig,
) => {
  const { ratePlans_TimeZone } = config;

  const canAmend = () =>
    isActive(subscription) &&
    !subscription.latestRateplans
      .filter(filterActiveRatePlans)
      .some((rp) => hasRatePlanActiveDiscounts(rp.ratePlanCharges, ratePlans_TimeZone)) &&
    !subscription.ratePlans.some((rp) =>
      hasRatePlanQtyChangeAmendmends(subscription.latestRateplans, rp),
    );

  const { maxAllowedQuantity = Infinity, minAllowedQuantity = 1 } = subscription;

  const isExpired = () =>
    getCurrentTimezoneDate(ratePlans_TimeZone).localeCompare(subscription.termEndDate) > 0;

  const isInNoticePeriod = () => {
    const noticePeriodDays = subscription.renewalTerm === 1 ? 14 : 30;
    const daysToTermEnd = getCurrentTimezoneDaysDiff(ratePlans_TimeZone, subscription.termEndDate);
    return daysToTermEnd < noticePeriodDays && daysToTermEnd >= 0;
  };

  const canIncreaseSeats = (currentSeats: number) => maxAllowedQuantity > currentSeats;

  const canDecreaseSeats = (currentSeats: number) => currentSeats > minAllowedQuantity;

  const canBeCancelled = () =>
    isActive(subscription) && !isExpired() && !isInNoticePeriod() && subscription.autoRenew;

  const canBeUncancelled = () =>
    !isExpired() &&
    subscription.defaultProductAutoRenew &&
    (!isActive(subscription) || (isActive(subscription) && !subscription.autoRenew));

  const canShowUpdateSeatButton = (currentSeats: number) =>
    currentSeats >= minAllowedQuantity && currentSeats <= maxAllowedQuantity;

  return {
    canAmend,
    canIncreaseSeats,
    canDecreaseSeats,
    canBeCancelled,
    canBeUncancelled,
    canShowUpdateSeatButton,
  };
};

export type SubscriptionCapacities = ReturnType<typeof makeSubscriptionCapacities>;

const isActive = (subscription: Subscription) => subscription.status === 'Active';

const filterActiveRatePlans = (ratePlan: SubscriptionRatePlan) =>
  ratePlan.lastChangeType !== 'Remove';
